/* eslint-disable no-var */
/* eslint-disable @typescript-eslint/no-namespace */
import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { supabase } from "./supabase";
import { logAuditEvent } from "../../auditoria/logAudit";
import {
  definirPerfilLeitura,
  perfilLeituraDoPapel,
  type PerfilLeitura,
} from "../../commons/somenteLeitura";

const baseAuthProvider = supabaseAuthProvider(supabase, {
  getIdentity: async () => {
    const user = await getUserFromCache();

    if (user == null) {
      throw new Error();
    }

    return {
      id: user.id,
      fullName: user.fullName,
      email: user.email,
      avatar: user.avatar,
      administrator: user.administrator,
      is_super_admin: user.is_super_admin,
      is_manager: user.is_manager,
      perfil_leitura: user.perfil_leitura,
      fluxo_caixa_nivel: user.fluxo_caixa_nivel ?? null,
      previsao_servopa: user.previsao_servopa ?? false,
      is_demo: user.is_demo ?? false,
      gestao_unidade: user.gestao_unidade ?? null,
    };
  },
});

const resetSessionState = () => {
  cachedUser = undefined;
  definirPerfilLeitura(null);
  // Legacy: clear the stale "currentProduct" key left behind by ProductContext.
  // Safe to drop after every active client has logged in once post-migration.
  localStorage.removeItem("currentProduct");

  // Clear persisted react-admin store entries for this app namespace.
  Object.keys(localStorage).forEach((key) => {
    if (key.includes("RaStore") || key.includes("CRM")) {
      localStorage.removeItem(key);
    }
  });
};

// Remove do navegador a sessão do Supabase (token, code-verifier, usuário),
// sem falar com o servidor. Chaves começam com o storageKey do cliente.
const apagarSessaoLocal = () => {
  const storageKey: string | undefined = (supabase.auth as any).storageKey;
  if (!storageKey) return;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(storageKey)) {
      localStorage.removeItem(key);
    }
  }
};

const forceNavigateWithCacheBust =(targetPath: string) => {
  if (typeof window === "undefined") {
    return;
  }

  const targetUrl = new URL(targetPath || "/", window.location.origin);
  targetUrl.searchParams.set("__reload", Date.now().toString());
  window.location.replace(targetUrl.toString());
};

export async function getIsInitialized() {
  if (getIsInitialized._is_initialized_cache == null) {
    const { data } = await supabase.from("init_state").select("is_initialized");

    getIsInitialized._is_initialized_cache = (data?.at(0)?.is_initialized ?? 0) > 0;
  }

  return getIsInitialized._is_initialized_cache;
}

export namespace getIsInitialized {
  export var _is_initialized_cache: boolean | null = null;
}

export const authProvider: AuthProvider = {
  ...baseAuthProvider,
  getIdentity: async () => {
    const user = await getUserFromCache();

    if (user == null) {
      throw new Error();
    }

    // Force cache refresh if is_super_admin is missing (migration fix)
    if (user.is_super_admin === undefined || user.is_manager === undefined || user.perfil_leitura === undefined || user.fluxo_caixa_nivel === undefined || user.previsao_servopa === undefined) {
      cachedUser = undefined;
      return authProvider.getIdentity?.() as any;
    }

    return {
      id: user.id,
      fullName: user.fullName,
      email: user.email,
      avatar: user.avatar,
      administrator: user.administrator,
      is_super_admin: user.is_super_admin,
      is_manager: user.is_manager,
      perfil_leitura: user.perfil_leitura,
      fluxo_caixa_nivel: user.fluxo_caixa_nivel ?? null,
      previsao_servopa: user.previsao_servopa ?? false,
      is_demo: user.is_demo ?? false,
      gestao_unidade: user.gestao_unidade ?? null,
    };
  },
  login: async (params) => {
    let result;
    try {
      result = await baseAuthProvider.login(params);
    } catch (error) {
      // Supabase Auth devolve "User is banned" em inglês, texto de erro de
      // API, não de tela — troca pelo aviso que a pessoa bloqueada precisa
      // ler, sem levantar o motivo (isso é conversa de quem bloqueou).
      const message = (error as any)?.message ?? "";
      if (/banned|user_banned/i.test(message)) {
        throw new Error("Acesso bloqueado. Contate o administrador da conta.");
      }
      throw error;
    }
    resetSessionState();

    // Uma sessão por pessoa (migrations 282 e 297): para todos, entrar aqui
    // encerra a conta nos outros lugares. Falhar nunca impede o login.
    try {
      const { data: unica } = await supabase.rpc("sessao_unica_vale_para_mim" as never);
      if (unica === true) await supabase.auth.signOut({ scope: "others" });
    } catch {
      // Sem a regra, a pessoa entra do mesmo jeito.
    }

    // Auditoria: registra o login (ator resolvido no banco via auth.uid()).
    void logAuditEvent({
      entity: "login",
      action: "login",
      metadata: { user_agent: navigator.userAgent },
    });

    // Demonstração: a carteira falsa é ancorada em `current_date` e se
    // reconstrói sozinha quando envelhece (migration 195) — sem isso, virado o
    // mês a apresentação abre com tudo zerado.
    //
    // `await`, e não `void`: logo abaixo a página recarrega, e a primeira tela
    // já lê a produção do mês. Disparar sem esperar seria corrida com o
    // redirect. No caminho comum (carteira já é de hoje, ou quem entrou não é
    // conta de demonstração) o RPC devolve na hora, sem escrever nada.
    //
    // Falhar aqui NUNCA pode impedir alguém de entrar: o login já aconteceu.
    try {
      await supabase.rpc("demo_garantir");
    } catch {
      // Demonstração desatualizada é aborrecimento; login barrado é bloqueio.
    }

    const redirectTo =
      typeof (params as any)?.redirectTo === "string" && (params as any).redirectTo
        ? (params as any).redirectTo
        : "/";

    forceNavigateWithCacheBust(redirectTo);
    return result;
  },
  logout: async (params) => {
    // Loga o logout ANTES de encerrar a sessão (enquanto auth.uid() ainda vale).
    await logAuditEvent({
      entity: "login",
      action: "logout",
      metadata: { user_agent: navigator.userAgent },
    });
    try {
      return await baseAuthProvider.logout(params);
    } catch (error) {
      // Sessão já encerrada no servidor (403 session_not_found): o auth-js
      // devolve AuthSessionMissingError, não ignora como faz com 401/404, e
      // NÃO apaga a sessão local — o botão Sair ficava sem efeito. Quem clicou
      // em Sair quer sair: apaga a sessão do navegador do mesmo jeito.
      console.warn("[auth] logout no servidor falhou; encerrando só local:", error);
      apagarSessaoLocal();
    } finally {
      resetSessionState();
    }
  },
  checkAuth: async (params) => {
    // Users are on the set-password page, nothing to do
    if (
      window.location.pathname === "/set-password" ||
      window.location.hash.includes("#/set-password")
    ) {
      return;
    }
    // Users are on the forgot-password page, nothing to do
    if (
      window.location.pathname === "/forgot-password" ||
      window.location.hash.includes("#/forgot-password")
    ) {
      return;
    }
    // Users are on the sign-up page, nothing to do
    if (
      window.location.pathname === "/sign-up" ||
      window.location.hash.includes("#/sign-up")
    ) {
      return;
    }

    const isInitialized = await getIsInitialized();

    // If not initialized, redirect to sign-up ONLY if no session exists
    // This prevents redirecting logged-in users to signup
    if (!isInitialized) {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) {
        await supabase.auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }
      // If user is logged in but not initialized, let them through
      // (edge case: database reset while user was logged in)
    }

    return baseAuthProvider.checkAuth(params);
  },
  checkError: async (error) => {
    const status = (error as any)?.status ?? (error as any)?.statusCode;

    // Keep session only when access is denied by role/scope.
    if (status === 403) {
      return;
    }

    // Invalid/expired session should still trigger auth flow.
    if (status === 401) {
      resetSessionState();
      throw error;
    }

    if (baseAuthProvider.checkError) {
      return baseAuthProvider.checkError(error);
    }

    return;
  },
  canAccess: async (params) => {
    const isInitialized = await getIsInitialized();
    if (!isInitialized) return false;

    // Get the current user
    const user = await getUserFromCache();
    if (user == null) {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData?.session) return false;

      const [{ data: superAdminByRpc }, { data: managerByRpc }] = await Promise.all([
        supabase.rpc("is_super_admin"),
        supabase.rpc("is_manager"),
      ]);

      const role = superAdminByRpc || managerByRpc ? "admin" : "user";
      return canAccess(role, params);
    }

    // Compute access rights from the user role
    const role = user.administrator ? "admin" : "user";
    return canAccess(role, params);
  },
};

let cachedUser: any;

// Chamar antes do refetch da identidade quando o próprio perfil muda (nome,
// avatar) — sem isso o menu lateral segue mostrando o valor antigo.
export const esquecerIdentidade = () => {
  cachedUser = undefined;
};

// Várias partes pedem a identidade ao mesmo tempo (getIdentity, canAccess,
// menu); sem isto cada uma refazia todos os pedidos abaixo.
let usuarioEmAndamento: Promise<any> | null = null;

const getUserFromCache = async () => {
  if (cachedUser != null) return cachedUser;
  if (!usuarioEmAndamento) {
    usuarioEmAndamento = montarUsuario().finally(() => {
      usuarioEmAndamento = null;
    });
  }
  return usuarioEmAndamento;
};

// Papéis do usuário: permissões e depois os papéis (sem JOIN, para evitar
// problemas do PostgREST). null = erro, como antes.
const lerPapeis = async (authUserId: string) => {
  const { data: permissions, error: errorPermissions } = await supabase
    .from("user_product_permissions")
    .select("role_id")
    .eq("user_id", authUserId)
    .eq("is_active", true);
  if (errorPermissions) return null;
  if (!permissions || permissions.length === 0) return [];

  const { data: roles, error: errorRoles } = await supabase
    .from("user_roles")
    .select("code, hierarchy_level")
    .in("id", permissions.map((p: any) => p.role_id));
  if (errorRoles) return null;
  return roles ?? [];
};

const montarUsuario = async () => {
  const { data: dataSession, error: errorSession } =
    await supabase.auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const authUser = dataSession.session.user;

  // Tudo em paralelo: nenhuma consulta depende da outra, e em fila eram 7 idas
  // ao servidor antes de a tela começar a pedir os próprios dados.
  // Fluxo de Caixa tem lista própria (migration 196) porque os quatro que usam
  // a aba já são super_admin — o papel global não separa quem lança de quem só
  // acompanha; Previsão Servopa, mesma ideia (migration 212). Cast pontual: as
  // RPCs ainda não estão em database.generated.ts.
  const [roles, { data: superAdminByRpc }, { data: managerByRpc }, { data: nivelFluxoCaixa }, { data: vePrevisaoServopa }, { data: userRow }] =
    await Promise.all([
      lerPapeis(authUser.id),
      supabase.rpc("is_super_admin"),
      supabase.rpc("is_manager"),
      (supabase as any).rpc("fluxo_caixa_nivel"),
      (supabase as any).rpc("previsao_servopa_pode_ver"),
      supabase
        .from("users")
        .select("avatar, name, is_demo")
        .eq("user_id", authUser.id)
        .maybeSingle(),
    ]);

  if (roles === null) {
    return undefined;
  }

  // User is administrator if they have super_admin role (hierarchy_level <= 1)
  // OR manager role (hierarchy_level = 20). As RPCs cobrem permissões fora de sincronia.
  const isSuperAdmin =
    roles.some((r: any) => r.code === "super_admin" || r.hierarchy_level <= 1) ||
    !!superAdminByRpc;
  const isManager =
    roles.some((r: any) => r.code === "manager" && r.hierarchy_level === 20) ||
    !!managerByRpc;
  const perfilLeitura: PerfilLeitura | null =
    roles.length > 0 ? perfilLeituraDoPapel(roles.map((r: any) => r.code)) : null;

  // Nome editado em "Minhas informações" fica em users.name; o metadata do
  // login guarda o nome do cadastro e não acompanha a edição.
  const name =
    userRow?.name?.trim() ||
    authUser.user_metadata?.name ||
    authUser.email?.split("@")[0] ||
    "User";

  const avatar =
    (typeof userRow?.avatar === "string"
      ? userRow.avatar
      : (userRow?.avatar as { src?: string } | null)?.src) ||
    authUser.user_metadata?.avatar_url;

  cachedUser = {
    id: authUser.id,
    fullName: name,
    email: authUser.email,
    avatar: avatar,
    administrator: isSuperAdmin || isManager || false,
    is_super_admin: authUser.user_metadata?.is_super_admin || isSuperAdmin || false,
    is_manager: isManager || false,
    perfil_leitura: perfilLeitura,
    fluxo_caixa_nivel: (nivelFluxoCaixa as "edicao" | "leitura" | null) ?? null,
    previsao_servopa: vePrevisaoServopa === true,
    // Conta de demonstração (migration 192): pula a busca de cotas e não
    // finaliza venda — nada sai para a administradora.
    is_demo: userRow?.is_demo === true,
    // Rede de escritórios (migration 308): administrador vê todas as
    // unidades; responsável, a dele. Os dois também contam como gerente.
    gestao_unidade: roles.some((r: any) => r.code === "admin_organizacao")
      ? "administrador"
      : roles.some((r: any) => r.code === "gerente_unidade")
        ? "responsavel"
        : null,
  };
  definirPerfilLeitura(perfilLeitura);

  return cachedUser;
};
